import React, { useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import Footer from 'components/Layout/Footer'
import { fetchCreateContactEmail } from 'src/redux/contact'
import { validateEmail } from 'data/helpers'

export default function ContactUs() {
  const dispatch = useDispatch()
  const theme = useTheme()

  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleChange = useCallback(
    name => e => {
      const value = e.target.value
      setValues(prev => ({ ...prev, [name]: value }))

      const newErrors = { ...errors }

      if (name === 'email') {
        if (!validateEmail(value)) {
          newErrors.email = 'Invalid email format.'
        } else {
          delete newErrors.email
        }
      }

      if (!value.trim()) {
        newErrors[name] = `${name.charAt(0).toUpperCase() + name.slice(1)} is required.`
      } else if (name !== 'email') {
        delete newErrors[name]
      }

      setErrors(newErrors)
    },
    [errors],
  )

  const handleSubmit = () => {
    const newErrors = {}
    if (!values.firstName.trim()) newErrors.firstName = 'First name is required.'
    if (!values.lastName.trim()) newErrors.lastName = 'Last name is required.'
    if (!values.email.trim()) newErrors.email = 'Email is required.'
    else if (!validateEmail(values.email)) newErrors.email = 'Invalid email format.'
    if (!values.message.trim()) newErrors.message = 'Message is required.'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    if (loading) return

    setLoading(true)
    dispatch(fetchCreateContactEmail({
      values,
      onSuccess: () => {
        setSubmitted(true)
        setLoading(false)
      },
      onFailure: () => {
        setLoading(false)
      },
    }))
  }

  if (submitted) {
    return (
      <>
        <Box
          sx={{
            backgroundColor: theme.palette.background.dark,
            color: theme.palette.common.white,
            py: { xs: 12, md: 18 },
            textAlign: 'center',
          }}
        >
          <Container maxWidth='sm'>
            <Typography
              variant='h3'
              sx={{ fontWeight: 700, mb: 3 }}
            >
              Thanks for Reaching Out
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '1.15rem',
                lineHeight: 1.7,
              }}
            >
              We've received your message and will get back to you within one business day.
            </Typography>
          </Container>
        </Box>
        <Footer />
      </>
    )
  }

  return (
    <>
      <Box
        sx={{
          backgroundColor: theme.palette.background.dark,
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth='md'>
          <Grid container spacing={6} alignItems='center'>
            <Grid item xs={12} md={5}>
              <Typography
                variant='h3'
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.2,
                  mb: 3,
                  color: theme.palette.primary.light,
                }}
              >
                Get in Touch
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '1.1rem',
                  lineHeight: 1.7,
                  mb: 4,
                }}
              >
                Have a question, feedback, or just want to say hello?
                We'd love to hear from you.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <EmailIcon sx={{ color: theme.palette.primary.light, fontSize: 20 }} />
                <Typography
                  component='a'
                  href='mailto:contact@mystethi.com'
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: '0.95rem',
                    textDecoration: 'none',
                    '&:hover': { color: theme.palette.common.white },
                  }}
                >
                  contact@mystethi.com
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  backgroundColor: theme.palette.common.white,
                  borderRadius: '12px',
                  p: { xs: 3, md: 4 },
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                }}
              >
                <Typography
                  variant='h5'
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    mb: 3,
                  }}
                >
                  Send Us a Message
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      fullWidth
                      label='First Name'
                      variant='outlined'
                      size='small'
                      value={values.firstName}
                      onChange={handleChange('firstName')}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                    />
                    <TextField
                      fullWidth
                      label='Last Name'
                      variant='outlined'
                      size='small'
                      value={values.lastName}
                      onChange={handleChange('lastName')}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    label='Email'
                    variant='outlined'
                    size='small'
                    value={values.email}
                    onChange={handleChange('email')}
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                  <TextField
                    fullWidth
                    label='How can we help?'
                    variant='outlined'
                    size='small'
                    multiline
                    rows={4}
                    value={values.message}
                    onChange={handleChange('message')}
                    error={!!errors.message}
                    helperText={errors.message}
                  />
                  <Button
                    fullWidth
                    variant='contained'
                    size='large'
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                      borderRadius: '8px',
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    }}
                  >
                    {loading ? <CircularProgress size={24} color='inherit' /> : 'Send Message'}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Footer />
    </>
  )
}
